import React, {
	useEffect,
	useMemo,
	useRef,
	useState,
	useCallback,
} from "react";
import MatchCard from "./MatchCard";

/**
 * Props:
 * - matches: Array of match objects (client-side pagination source)
 * - filterThreshold: number — filter by match.match_score
 * - mode, redThreshold, yellowThreshold: forwarded to MatchCard
 * - pageSize?: number (default 1000)
 * - fetchMore?: async function () => Promise<{ items: any[], hasMore?: boolean }>
 *      Optional. If provided, will be called when we've shown all local items
 *      and user scrolls to the sentinel. Append results to the list.
 * - getKey?: function(match, idx) => string
 */
export default function MatchResultsList({
	matches,
	filterThreshold = 0,
	mode,
	redThreshold,
	yellowThreshold,
	pageSize = 1000,
	fetchMore, // optional async loader (server-side pagination)
	getKey, // optional stable key getter
}) {
	const scrollRef = useRef(null);
	const sentinelRef = useRef(null);
	const observerRef = useRef(null);

	// Filter once, then paginate the filtered result
	const filtered = useMemo(
		() =>
			Array.isArray(matches)
				? matches.filter((m) => (m?.match_score ?? 0) >= filterThreshold)
				: [],
		[matches, filterThreshold]
	);

	// How many items we’re currently showing from the (filtered) list:
	const [visibleCount, setVisibleCount] = useState(pageSize);

	// When fetchMore is provided, we can append remote pages as well:
	const [remoteItems, setRemoteItems] = useState([]);
	const [isLoadingMore, setIsLoadingMore] = useState(false);
	const [hasMoreRemote, setHasMoreRemote] = useState(true); // unknown until fetchMore returns false

	// Reset pagination when matches or filter changes
	useEffect(() => {
		setVisibleCount(pageSize);
		setRemoteItems([]);
		setHasMoreRemote(true);
	}, [filtered, pageSize]);

	// Create the combined list we actually render
	const renderItems = useMemo(() => {
		const localSlice = filtered.slice(0, visibleCount);
		return remoteItems.length ? [...localSlice, ...remoteItems] : localSlice;
	}, [filtered, visibleCount, remoteItems]);

	const loadNextChunk = useCallback(async () => {
		// 1) First, try to grow local visibility (client-side)
		if (visibleCount < filtered.length) {
			setVisibleCount((prev) => Math.min(prev + pageSize, filtered.length));
			return;
		}

		// 2) If we’ve shown all local filtered items, fall back to remote fetch (if available)
		if (!fetchMore || isLoadingMore || !hasMoreRemote) return;

		try {
			setIsLoadingMore(true);
			const res = await fetchMore();
			const newItems = Array.isArray(res?.items) ? res.items : [];
			setRemoteItems((prev) => [...prev, ...newItems]);

			// If the API tells us there are no more, respect it; otherwise infer from page size
			if (typeof res?.hasMore === "boolean") {
				setHasMoreRemote(res.hasMore);
			} else {
				setHasMoreRemote(newItems.length > 0); // naive fallback
			}
		} catch (e) {
			// On error, allow trying again
			console.error("fetchMore failed:", e);
		} finally {
			setIsLoadingMore(false);
		}
	}, [
		filtered.length,
		visibleCount,
		pageSize,
		fetchMore,
		isLoadingMore,
		hasMoreRemote,
	]);

	// IntersectionObserver to auto-load when the sentinel is near the bottom
	useEffect(() => {
		if (!sentinelRef.current || !scrollRef.current) return;

		if (observerRef.current) observerRef.current.disconnect();

		observerRef.current = new IntersectionObserver(
			(entries) => {
				const first = entries[0];
				if (first?.isIntersecting) loadNextChunk();
			},
			{
				root: scrollRef.current, // observe within the scrollable container
				rootMargin: "600px", // start loading a bit before we hit the end
				threshold: 0.0,
			}
		);

		observerRef.current.observe(sentinelRef.current);
		return () => observerRef.current && observerRef.current.disconnect();
	}, [loadNextChunk]);

	if (!filtered || filtered.length === 0) {
		return (
			<div
				style={{
					maxHeight: "60vh",
					overflowY: "auto",
					border: "1px solid #ccc",
					padding: 10,
					borderRadius: 4,
				}}>
				<p style={{ color: "#666", fontStyle: "italic" }}>
					No matches found with score above {filterThreshold.toFixed(2)}.
				</p>
			</div>
		);
	}

	return (
		<div
			ref={scrollRef}
			style={{
				maxHeight: "60vh",
				overflowY: "auto",
				border: "1px solid #ccc",
				padding: 10,
				borderRadius: 4,
			}}>
			{renderItems.map((match, idx) => (
				<MatchCard
					key={
						getKey
							? getKey(match, idx)
							: match.id ??
							  match.existing_lead_id ??
							  `${idx}-${match.uploaded_index ?? "u"}-${
									match.best_db_index ?? "b"
							  }`
					}
					match={match}
					mode={mode}
					redThreshold={redThreshold}
					yellowThreshold={yellowThreshold}
				/>
			))}

			{/* Bottom bar */}
			<div style={{ display: "flex", justifyContent: "center", padding: 12 }}>
				{/* If we still have local or remote to load, show a manual button as well */}
				{visibleCount < filtered.length || (fetchMore && hasMoreRemote) ? (
					<button
						onClick={loadNextChunk}
						disabled={isLoadingMore}
						style={{
							padding: "6px 12px",
							borderRadius: 6,
							border: "1px solid #ddd",
							background: "#fafafa",
							cursor: isLoadingMore ? "not-allowed" : "pointer",
						}}
						title="Load more">
						{isLoadingMore ? "Loading…" : "Load more"}
					</button>
				) : (
					<div style={{ color: "#666" }}>No more results.</div>
				)}
			</div>

			{/* Invisible sentinel for auto-load */}
			<div ref={sentinelRef} aria-hidden style={{ height: 1, width: "100%" }} />
		</div>
	);
}
